import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Loader2, AlertCircle } from 'lucide-react'
import { buildApiUrl } from '@/lib/config'

// Component to handle magic link redirection
function MagicLinkRedirector() {
  const [status, setStatus] = useState<'loading' | 'error'>('loading')
  const [error, setError] = useState<string>('')

  useEffect(() => {
    const handleRedirect = async () => {
      try {
        // Parse the search parameters
        const searchParams = new URLSearchParams(window.location.search)
        const token = searchParams.get('token')
        const org = searchParams.get('org')
        const redirectUrl = searchParams.get('redirect')

        if (!token || !org) {
          setStatus('error')
          setError('Invalid magic link. Missing required parameters.')
          return
        }

        // Fetch organization info to get the subdomain
        const response = await fetch(buildApiUrl(`/organization-portal/validate-by-id/${org}`))
        
        if (!response.ok) {
          setStatus('error')
          // Only a 404 means the organization is gone; anything else is a server problem worth retrying
          setError(response.status === 404
            ? 'This organization could not be found. It may no longer be active.'
            : 'An error occurred while processing your magic link. Please try again.')
          return
        }

        const result = await response.json()
        
        if (!result.success || !result.data) {
          setStatus('error')
          setError(result.message || 'Organization validation failed.')
          return
        }

        // Build the correct portal verify URL
        const orgData = result.data
        let verifyUrl = `/org/${orgData.subdomain}/portal/verify?token=${encodeURIComponent(token)}&org=${encodeURIComponent(org)}`
        
        if (redirectUrl) {
          verifyUrl += `&redirect=${encodeURIComponent(redirectUrl)}`
        }

        // Redirect to the correct portal verify URL
        window.location.href = verifyUrl
      } catch (error) {
        console.error('Error redirecting magic link:', error)
        setStatus('error')
        setError('An error occurred while processing your magic link. Please try again.')
      }
    }

    handleRedirect()
  }, [])

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <Card className="w-full max-w-md mx-auto">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
              <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
            </div>
            <CardTitle className="text-xl">Processing Magic Link</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center text-gray-600 dark:text-gray-400">
              <p>Please wait while we redirect you to your portal...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
            <AlertCircle className="h-6 w-6 text-red-600" />
          </div>
          <CardTitle className="text-xl">Error</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-red-600">
            <p>{error}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export const Route = createFileRoute('/auth/magic-link')({
  component: MagicLinkRedirector,
})